/* eslint-disable react-hooks/rules-of-hooks */
import { ArrowLeft } from '@emotion-icons/fa-solid/ArrowLeft'
import { Close } from '@emotion-icons/material/Close'
import { Search } from '@emotion-icons/fa-solid/Search'
import styled from '@emotion/styled'
import { Autocomplete, IconButton, Popper, TextField, Typography } from '@mui/material'
import { KeyboardEventHandler } from 'react'
import { ButtonWithIcon } from 'src/components/button/button-with-icon'
import { FlexColumn } from 'src/components/flex-containers'
import { Colors, Shapes, Typographies, useTypography } from 'src/components/theme'
import { FANCY_SCROLLBAR } from 'src/utils/styles'

const Wrapper = styled.div`
  position: relative;
  flex-grow: 1;
`
// Style the autocomplete so that it looks like a search input with rounded corners
const AutoComplete = styled(Autocomplete)`
  position: relative;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  border-radius: ${Shapes.borderRadius}px;
  height: ${Shapes.spacing[7]}px;
  overflow: hidden;
  & .MuiFormControl-root {
    height: 100%;
    width: 100%;
    & .MuiInputBase-root {
      height: 100%;
      width: 100%;
      padding: 0;
      display: flex;
      align-items: stretch;
      input {
        height: 100%;
        padding: 0 !important;
        padding-inline: ${Shapes.spacing[2]}px !important;
        background-color: ${Colors.bgWhite};
        ${useTypography(Typographies.textmdm, true)}
      }
      .Mui-disabled {
        -webkit-text-fill-color: ${Colors.neutral300};
        color: ${Colors.neutral300};
      }
    }
  }
` as typeof Autocomplete

// Replace the Popper/Paper/Listbox related components with better styled components
const Poppr = styled(Popper)`
  margin-top: ${Shapes.spacing[2]}px !important;
  .Mui-Popper-root {
    padding: 0 !important;
  }
  box-shadow: ${Colors.BOX_SHADOW} !important;
  border-radius: ${Shapes.borderRadius}px;
  overflow: hidden;
`
const Paper = styled.div`
  background: none !important;
  overflow: hidden !important;
`
const ListBox = styled.div`
  background-color: ${Colors.bgWhite};
  height: 100%;
  max-height: calc(100vh - 62px) !important; // 62px = paddings + search input height
  padding: 0 !important;
  & .Mui-focused {
    background-color: ${Colors.neutral50} !important;
    p {
      color: ${Colors.neutral800} !important;
    }
    span {
      color: ${Colors.neutral500} !important;
    }
    & svg {
      fill: ${Colors.neutral700};
      color: ${Colors.neutral700};
      transform: scale(1.2);
      transition: all 0.2s ease-in-out;
    }
  }
  ${FANCY_SCROLLBAR}
`

// Style the list items to have a hover effect and truncate text
const ListItem = styled.li`
  display: flex;
  align-items: stretch;
  padding: ${Shapes.spacing[3]}px !important;
  gap: ${Shapes.spacing[2]}px !important;
  :hover {
    cursor: pointer;
  }
  // Icon
  svg {
    flex: 0 0 auto;
  }
  div {
    flex: 1;
    overflow: hidden;
    // Title
    p {
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
    }
    // Subtitle
    & > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }
`

// Wrappers to handle adornments (where additional components can be added to either side of the input field)
const StartAdornmentWrapper = styled.div`
  display: flex;
  height: 100%;
`
const StartAdornmentDivider = styled.div<{ background?: string }>`
  height: 100%;
  width: 1px;
  background-color: ${Colors.border};
`
const StartAdornmentIcon = styled.span`
  padding-left: ${Shapes.spacing[2]}px;
  align-items: center;
  display: flex;
  align-self: stretch;
  background-color: ${Colors.bgWhite};
`
const EndAdornmentWrapper = styled(StartAdornmentWrapper)`
  display: flex;
  gap: ${Shapes.spacing[2]}px;
  background-color: ${Colors.bgWhite};
`

const BackToResults = styled(ButtonWithIcon)`
  position: absolute;
  bottom: -${Shapes.spacing[6]}px;
  width: 100%;
  border: none;
  border-top: 1px solid ${Colors.border};
  border-radius: ${Shapes.borderRadius}px;
  border-top-left-radius: 0;
  border-top-right-radius: 0;
  padding: ${Shapes.spacing[2]}px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${Shapes.spacing[2]}px;
  background-color: ${Colors.bgWhite};
  box-shadow: ${Colors.BOX_SHADOW};
  :hover {
    background-color: ${Colors.neutral50};
    text-decoration: underline;
    box-shadow: ${Colors.BOX_SHADOW};
  }
`

export type SearchOption =
  | {
      id: string
      icon?: React.ReactNode
      title: string
      subtitle?: string
      value?: string
    }
  | undefined

export type SearchInputProps = {
  id?: string
  icon?: React.ReactNode
  placeholder?: string
  search: string
  options?: Array<SearchOption>
  value?: SearchOption
  onFocus?: () => void
  onBlur?: (event: React.FocusEvent<HTMLDivElement, Element>) => void
  onInputChange: (value: string) => void
  onChange?: (value: SearchOption) => void
  onEnter?: () => void
  onClear?: () => void
  startAdornment?: React.ReactNode
  endAdornment?: React.ReactNode
  backToResults?: boolean
  onBackToResults?: () => void
  disabled?: boolean
  shadow?: boolean
  inputRef?: React.RefObject<HTMLInputElement>
  style?: React.CSSProperties
  endAdornmentFullSize?: boolean
}
export const SearchInput = (props: SearchInputProps) => {
  const {
    id,
    icon,
    placeholder,
    search,
    options,
    value,
    onFocus,
    onBlur,
    onInputChange,
    onChange,
    onEnter,
    onClear,
    startAdornment,
    endAdornment,
    backToResults,
    onBackToResults,
    disabled,
    shadow = true,
    inputRef,
    style,
    endAdornmentFullSize = false,
  } = props

  const onKeyUp: KeyboardEventHandler<HTMLLIElement | HTMLDivElement> = (e) => {
    // Allows us to use arrow keys + enter to select a suggestion without triggering an 'onEnter' search
    if (e.key === 'Enter' && !e.nativeEvent.defaultPrevented) {
      onEnter && onEnter()
    }
  }

  return (
    <Wrapper id={id}>
      <AutoComplete
        style={{
          boxShadow: shadow ? Colors.BOX_SHADOW : 'none',
          border: !shadow ? `1px solid ${Colors.border}` : 'none',
          ...style,
        }}
        autoComplete
        freeSolo
        includeInputInList
        filterSelectedOptions
        fullWidth
        size="small"
        disabled={disabled}
        getOptionLabel={(option) => {
          return option ? (typeof option === 'string' ? option : option.value || option.title) : ''
        }}
        inputValue={search}
        value={value}
        options={options || []}
        filterOptions={(x) => x}
        onFocus={onFocus}
        onBlur={onBlur}
        onInputChange={(_, newValue) => onInputChange(newValue)}
        onChange={(_, newValue) => {
          if (newValue === null) {
            return
          }
          // Do a 'press enter' search
          if (typeof newValue === 'string') {
            onEnter && onEnter()
            return
          }
          // Select a suggestion
          if (newValue) {
            onChange && onChange(newValue)
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            variant="standard"
            placeholder={placeholder}
            inputRef={inputRef}
            value={search}
            onKeyUp={onKeyUp}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              startAdornment: (
                <StartAdornmentWrapper>
                  <>
                    {startAdornment}
                    {startAdornment && <StartAdornmentDivider />}
                  </>
                  {icon && <StartAdornmentIcon>{icon}</StartAdornmentIcon>}
                </StartAdornmentWrapper>
              ),
              endAdornment: (
                <EndAdornmentWrapper style={{ paddingRight: endAdornmentFullSize ? 0 : Shapes.spacing[2] }}>
                  {!!search && (
                    <IconButton
                      style={{
                        alignSelf: 'center',
                      }}
                      size="small"
                      onClick={() => {
                        onInputChange('')
                        onChange && onChange(undefined)
                        onClear && onClear()
                      }}>
                      <Close size={16} color={Colors.neutral400} />
                    </IconButton>
                  )}
                  {endAdornment}
                </EndAdornmentWrapper>
              ),
            }}
          />
        )}
        renderOption={(props, option) =>
          option ? (
            <ListItem
              {...props}
              key={option.id}
              onKeyUp={(e) => {
                // Prevent the selection of an option propogating to the parent input when pressing enter
                e.preventDefault()
                e.stopPropagation()
                e.nativeEvent.preventDefault()
                e.nativeEvent.stopImmediatePropagation()
                onKeyUp(e)
              }}>
              {option.icon || <Search size={16} color={Colors.neutral400} />}
              <FlexColumn gap={2}>
                <Typography variant="textmd" color={Colors.neutral400}>
                  {option.title}
                </Typography>
                {option.subtitle && (
                  <Typography variant="textmdm" color={Colors.neutral300}>
                    <em>{option.subtitle}</em>
                  </Typography>
                )}
              </FlexColumn>
            </ListItem>
          ) : null
        }
        PopperComponent={Poppr}
        PaperComponent={Paper}
        ListboxComponent={ListBox}
        sx={{
          '&': {
            borderBottomLeftRadius: backToResults ? 0 : `${Shapes.borderRadius}px`,
            borderBottomRightRadius: backToResults ? 0 : `${Shapes.borderRadius}px`,
          },
        }}
      />
      {backToResults && (
        <BackToResults onClick={onBackToResults} color={Colors.neutral600}>
          <ArrowLeft size={16} />
          Back to results
        </BackToResults>
      )}
    </Wrapper>
  )
}
